// AI Equipment Suggestions API helper
// Communicates with Falcon backend /api/v1/ai-equipment-suggestions endpoint
// Mirrors response shape defined in falcon/handlers/ai_equipment_suggestions.py
import { authenticatedJsonHeaders } from "./authenticatedHeaders";
import { v4 as uuid } from "uuid";

export type AIOptimizationPriority = "performance" | "runlife" | "cost";

export type AIEquipmentType =
  | "pump"
  | "pump_housing"
  | "motor"
  | "intake"
  | "seal"
  | "sensor"
  | "mle"
  | "downhole_cable"
  | "surface_cable"
  | "vsd"
  | "switchboard"
  | "step_up_transformer"
  | "step_down_transformer";

export interface AIEquipmentIssueDetail {
  label: string;
  value: string | number;
  unit?: string | null;
  relaxed?: boolean;
}

export interface AIEquipmentIssue {
  code: string;
  message: string;
  equipmentType: AIEquipmentType;
  details?: AIEquipmentIssueDetail[];
}

export interface AIEquipmentSuggestionsRequest {
  jobId?: string;
  scenarioId?: string;
  sizingId?: string;
  wellProperties?: Record<string, unknown>;
  parameters?: Record<string, unknown>;
  optimizationPriority?: AIOptimizationPriority;
  enhanceNarrative?: boolean;
}

export interface AIEquipmentSuggestionMetrics {
  targetFlowRate: number;
  score?: number;
  efficiency: number | null;
  expectedLife?: number | null;
  costSavings?: number | null;
  powerRequirement: string;
  bepProximityPercent: number;
  motorMarginPercent: number;
  freeGasFraction: number | null;
}

export interface AIEquipmentSuggestionEquipment {
  pumps: Array<Record<string, unknown>>;
  motor: { ratedHP: number; estimatedLoadHP: number };
  intake: Record<string, unknown>;
  intakes?: Array<Record<string, unknown>>;
  cable: Record<string, unknown>;
  seal?: Record<string, unknown>;
  seals?: Array<Record<string, unknown>>;
  sensor?: Record<string, unknown>;
  mle?: Record<string, unknown>;
  surfaceCable?: Record<string, unknown>;
  vsd?: Record<string, unknown>;
  switchboard?: Record<string, unknown>;
  stepUpTransformer?: Record<string, unknown>;
  stepDownTransformer?: Record<string, unknown>;
}

// Calculation-local xrefs are remapped to revision-backed form records on apply.
export interface AISizingDraft {
  version: 1 | 2;
  parameters?: Record<string, number>;
  equipment: Record<string, unknown>;
  pumps: Record<string, Record<string, unknown>>;
  housings: Record<string, Record<string, unknown>>;
  intakes: Record<string, Record<string, unknown>>;
  seals: Record<string, Record<string, unknown>>;
  cables: Record<string, Record<string, unknown>>;
}

export interface AIEquipmentSuggestionOption {
  rankingDiagnostics?: {
    version: string;
    appliedWeights: Record<string, number>;
    unpenalizedScore: number;
    issuePenalty: number;
  } | null;
  searchSummary?: {
    staged?: boolean;
    operatingPointPassing?: number;
    fullVerifications?: number;
    duplicatePackagesAvoided?: number;
    phaseSeconds?: Record<string, number>;
    totalSeconds?: number;
    engineCalculations?: Record<string, number>;
    catalogCache?: { hits: number; loads: number };
    attemptedCandidates: number;
    verifiedCandidates: number;
    comparisonTarget: number;
    stopReason: string;
    scope: string;
  } | null;
  designPlan?: Record<string, unknown>;
  technicalEvidence?: Array<{ id: string; documentId: string; revision: string; page: number; text: string }>;
  incomplete?: boolean;
  completionRequirements?: Array<{
    code: string;
    equipmentType: string;
    message: string;
    action: string;
  }>;
  priority: AIOptimizationPriority;
  metrics: AIEquipmentSuggestionMetrics;
  equipment: AIEquipmentSuggestionEquipment;
  sizingDraft?: AISizingDraft | null;
  warnings: string[];
  traceId?: string | null;
  verification?: {
    scope?: string;
    engineVersion?: string;
    engineHash?: string;
    inputHash?: string;
    candidateHash?: string;
    classificationThresholds?: Record<string, unknown>;
  };
  verifiedMetrics?: Record<string, { value: number | null; unit: string }>;
  confidence?: string | null;
  scoreBreakdown?: Record<string, number>;
  pros?: string[];
  cons?: string[];
}

export interface AIEquipmentSuggestionsContext {
  sourceFingerprint?: string;
  scenarioId?: string;
  sizingId?: string;
  targetLiquidRateStbd?: number;
  wellTestLiquidRateStbd?: number;
  requiredLift?: number;
  recommendedFrequency?: number;
  rorMin?: number;
  rorMax?: number;
  rorBep?: number;
  scenarioHash?: string;
}

export interface AIEquipmentSuggestionsResponse {
  laneDiagnostics?: Record<string, unknown>;
  success: boolean;
  pipelineVersion?: string;
  narrativeVersion?: string;
  enhancementIds?: {
    performance?: string;
    runlife?: string;
    cost?: string;
  };
  narrativePending?: boolean;
  context: AIEquipmentSuggestionsContext;
  options: {
    performance: AIEquipmentSuggestionOption;
    runlife: AIEquipmentSuggestionOption;
    cost: AIEquipmentSuggestionOption;
  };
  selectedPriority: AIOptimizationPriority;
  warnings: string[];
  source?: string;
  agentcore?: Record<string, unknown>;
  blockingReasons?: AIEquipmentIssue[];
  equipmentIssues?: AIEquipmentIssue[];
  error?: string;
}

interface AIEquipmentSuggestionsErrorResponse {
  message?: string;
  blockingReasons?: Array<{ message?: string }>;
  failureSummary?: Array<{ code?: string; count?: number }>;
  agentcoreError?: { message?: string };
}

/**
 * Error thrown when equipment suggestions fetch fails
 */
export class AIEquipmentSuggestionsError extends Error {
  constructor(
    message: string,
    public status?: number,
    public responseText?: string,
  ) {
    super(message);
    this.name = "AIEquipmentSuggestionsError";
  }
}

function formatErrorResponse(status: number, responseText: string): string {
  let body: AIEquipmentSuggestionsErrorResponse;
  try {
    body = JSON.parse(responseText);
  } catch {
    return `AI equipment suggestions failed (${status}).`;
  }

  const details = new Set<string>();
  for (const reason of body.blockingReasons || []) {
    if (reason?.message) details.add(reason.message);
  }
  for (const failure of body.failureSummary || []) {
    if (!failure?.code) continue;
    const label = failure.code.replace(/_/g, " ");
    details.add(
      failure.count && failure.count > 1
        ? `${label} (${failure.count} candidates)`
        : label,
    );
  }
  if (!details.size && body.agentcoreError?.message) {
    details.add(body.agentcoreError.message);
  }

  const message =
    body.message || `AI equipment suggestions failed (${status}).`;
  return [message, ...details].join(" ");
}

/**
 * Resolve the full URL for the suggestions endpoint
 */
function resolveBaseUrl(): string {
  const explicitBase = (import.meta.env.VITE_FALCON_BASE || "").replace(
    /\/$/,
    "",
  );
  const isDev = import.meta.env.DEV;
  if (isDev && !explicitBase) return "/api/v1/ai-equipment-suggestions";
  if (explicitBase) return `${explicitBase}/api/v1/ai-equipment-suggestions`;
  return "/api/v1/ai-equipment-suggestions";
}

/**
 * Fetch AI equipment suggestions for a scenario.
 *
 * @param payload - Request payload with scenario data
 * @returns Promise resolving to equipment suggestions
 * @throws AIEquipmentSuggestionsError if the request fails
 *
 * @example
 * ```typescript
 * const suggestions = await fetchAIEquipmentSuggestions({
 *   scenarioId: 'scenario_123',
 *   optimizationPriority: 'performance',
 *   enhanceNarrative: true
 * });
 *
 * const performanceOption = suggestions.options.performance;
 * console.log('BEP Proximity:', performanceOption.metrics.bepProximityPercent);
 *
 * // Poll for narratives if requested
 * if (suggestions.enhancementIds && suggestions.narrativePending) {
 *   // Use pollNarrativeStatus from aiEquipmentNarrative.ts for each priority
 * }
 * ```
 */
export async function fetchAIEquipmentSuggestions(
  payload: AIEquipmentSuggestionsRequest,
  onProgress?: (progress: { stage?: string; candidate?: number; verified?: number; elapsedSeconds?: number;
    jobId?: string; partialOptions?: Partial<AIEquipmentSuggestionsResponse["options"]>; context?: AIEquipmentSuggestionsContext }) => void,
  isSuperseded?: () => boolean,
): Promise<AIEquipmentSuggestionsResponse> {
  const url = resolveBaseUrl();

  try {
    // Parent queue + planning + independent lane queue/execution windows.
    const deadline = Infinity; // Monitoring can reconnect; only execution is time-bounded server-side.
    const requestId = uuid();
    let requestBody: AIEquipmentSuggestionsRequest & { jobId?: string; requestId?: string } =
      { ...payload, requestId };
    let transientFailures = 0;
    while (Date.now() < deadline) {
      if (isSuperseded?.()) throw new AIEquipmentSuggestionsError("Calculation monitoring stopped.");
      // Retry only polls for an already accepted job. Reposting an initial
      // request after an ambiguous network failure could create duplicate work.
      try {
      const res = await fetch(url, {
        method: "POST",
        headers: await authenticatedJsonHeaders(),
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout(Math.min(30000, Math.max(1, deadline - Date.now()))),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new AIEquipmentSuggestionsError(
          formatErrorResponse(res.status, text),
          res.status,
          text,
        );
      }

      const data = await res.json();
      transientFailures = 0;
      if (res.status === 202) {
        if (typeof data.jobId !== "string") {
          throw new AIEquipmentSuggestionsError(
            "Equipment calculation was accepted but did not return a job ID.",
            res.status,
            JSON.stringify(data),
          );
        }
        requestBody = { ...payload, requestId, jobId: data.jobId };
        onProgress?.({ ...data.progress, jobId: data.jobId, partialOptions: data.partialOptions, context: data.context,
          stage: data.progress?.stage || "Queued for equipment calculation",
          elapsedSeconds: data.progress?.executionSeconds ?? data.progress?.elapsedSeconds ??
            (data.requestedAt ? Math.max(0, Math.floor((Date.now() - Date.parse(data.requestedAt)) / 1000)) : undefined) });
        await new Promise((resolve) => setTimeout(resolve, 2000));
        continue;
      }
      return data;
      } catch (error) {
        const transient = !(error instanceof AIEquipmentSuggestionsError) ||
          [429, 502, 503, 504].includes(error.status || 0);
        if (!requestBody.jobId || !transient || ++transientFailures > 4) throw error;
        const delay = Math.min(1000 * 2 ** (transientFailures - 1), 8000);
        await new Promise(resolve => setTimeout(resolve, Math.min(delay, Math.max(0, deadline - Date.now()))));
      }
    }
    throw new AIEquipmentSuggestionsError(
      "Equipment calculation is taking too long. Please retry.",
    );
  } catch (error) {
    if (error instanceof AIEquipmentSuggestionsError) {
      throw error;
    }
    // Network or parsing error
    throw new AIEquipmentSuggestionsError(
      `Network error fetching suggestions: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

export async function cancelAIEquipmentJob(scenarioId: string, jobId: string): Promise<void> {
  const response = await fetch(resolveBaseUrl(), {
    method: "POST", headers: await authenticatedJsonHeaders(),
    body: JSON.stringify({ scenarioId, jobId, cancel: true }),
    signal: AbortSignal.timeout(30000)
  });
  if (!response.ok) throw new AIEquipmentSuggestionsError(
    formatErrorResponse(response.status, await response.text()), response.status);
}

/**
 * Pick an option by priority from the response.
 * Falls back to selectedPriority if no priority specified.
 *
 * @param resp - The suggestions response
 * @param priority - Optional priority to select (defaults to response.selectedPriority)
 * @returns The selected option or null if not found
 */
export function pickOptionByPriority(
  resp: AIEquipmentSuggestionsResponse,
  priority?: AIOptimizationPriority,
): AIEquipmentSuggestionOption | null {
  const p = priority || resp.selectedPriority;
  if (p === "performance") return resp.options.performance;
  if (p === "runlife") return resp.options.runlife;
  if (p === "cost") return resp.options.cost;
  return resp.options[p] || null;
}

/**
 * Get all three options as an array for easy iteration.
 *
 * @param resp - The suggestions response
 * @returns Array of all options in order [performance, runlife, cost]
 */
export function getAllOptions(
  resp: AIEquipmentSuggestionsResponse,
): AIEquipmentSuggestionOption[] {
  return [resp.options.performance, resp.options.runlife, resp.options.cost];
}

/**
 * Aggregate warnings from top-level and chosen option.
 * Combines and deduplicates warnings.
 *
 * @param resp - The suggestions response
 * @param option - Optional specific option (if null, only returns top-level warnings)
 * @returns Sorted array of unique warnings
 */
export function aggregateWarnings(
  resp: AIEquipmentSuggestionsResponse,
  option?: AIEquipmentSuggestionOption | null,
): string[] {
  const set = new Set<string>(resp.warnings || []);
  if (option?.warnings) {
    option.warnings.forEach((w) => set.add(w));
  }
  return Array.from(set).sort();
}

/**
 * Get all warnings across all options.
 *
 * @param resp - The suggestions response
 * @returns Sorted array of all unique warnings from all options
 */
export function getAllWarnings(resp: AIEquipmentSuggestionsResponse): string[] {
  const set = new Set<string>(resp.warnings || []);

  getAllOptions(resp).forEach((option) => {
    if (option?.warnings) {
      option.warnings.forEach((w) => set.add(w));
    }
  });

  return Array.from(set).sort();
}

/**
 * Check if narrative enhancement is available and pending.
 *
 * @param resp - The suggestions response
 * @returns True if enhancement was requested and is pending
 */
export function hasNarrativePending(
  resp: AIEquipmentSuggestionsResponse,
): boolean {
  return Boolean(resp.enhancementIds && resp.narrativePending);
}

/**
 * Get warning severity based on warning code.
 * Useful for UI styling.
 *
 * @param warningCode - The warning code string
 * @returns Severity level: 'critical' | 'warning' | 'info'
 */
export function getWarningSeverity(
  warningCode: string,
): "critical" | "warning" | "info" {
  // Critical warnings that indicate equipment may not work
  const critical = [
    "motor_hp_below_calculated_requirement",
    "motor_margin_percent_negative",
    "motor_load_high",
    "missing_operating_window",
    "free_gas_fraction_null",
  ];

  // Info-level warnings
  const info = ["cable_gauge_mismatch"];

  if (critical.some((c) => warningCode.includes(c))) {
    return "critical";
  }

  if (info.some((i) => warningCode.includes(i))) {
    return "info";
  }

  return "warning";
}

/**
 * Format warning code into human-readable message.
 *
 * @param warningCode - The warning code string
 * @returns Human-readable warning message
 */
export function formatWarningMessage(warningCode: string): string {
  const messages: Record<string, string> = {
    cable_gauge_mismatch: "Cable gauge may not match optimal selection",
    motor_margin_percent_negative:
      "Motor is overloaded - increase motor capacity",
    motor_hp_below_calculated_requirement:
      "Motor nameplate HP is below the calculated load requirement",
    motor_load_low: "Motor load is below 70% of rated capacity",
    motor_load_high: "Motor load is above 130% of rated capacity",
    bep_proximity_low: "Operating point is far from best efficiency point",
    free_gas_fraction_null:
      "Free gas fraction could not be calculated - missing data",
    free_gas_high: "Gas entering the pump exceeds 25%",
    pump_efficiency_low:
      "Pump efficiency is below the minimum for this recommendation priority",
    missing_operating_window: "Operating window data is incomplete",
    baseline_component_reused:
      "No catalog candidate matched, so the existing sizing component was retained",
  };

  return messages[warningCode] || warningCode.replace(/_/g, " ");
}
